import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SocialProof = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const clientLogos = [
    {
      name: "TechCorp Global",
      logo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop&crop=center",
      industry: "Technology"
    },
    {
      name: "Manufacturing Plus",
      logo: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=120&h=60&fit=crop&crop=center",
      industry: "Manufacturing"
    },
    {
      name: "FinanceFlow",
      logo: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=120&h=60&fit=crop&crop=center",
      industry: "Financial Services"
    },
    {
      name: "HealthTech Solutions",
      logo: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=120&h=60&fit=crop&crop=center",
      industry: "Healthcare"
    },
    {
      name: "RetailMax",
      logo: "https://images.unsplash.com/photo-1560472355-536de3962603?w=120&h=60&fit=crop&crop=center",
      industry: "Retail"
    },
    {
      name: "EnergyCore",
      logo: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=120&h=60&fit=crop&crop=center",
      industry: "Energy"
    }
  ];

  const testimonials = [
    {
      id: 1,
      quote: `AI Clean Core transformed our approach to artificial intelligence. We went from spending $2M annually on vendor solutions to building our own AI capabilities that actually understand our business. The ROI was evident within six months.`,
      author: "Sarah Chen",
      title: "Chief Technology Officer",
      company: "TechCorp Global",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=80&h=80&fit=crop&crop=face",
      metrics: "60% cost reduction, 3x faster deployment"
    },
    {
      id: 2,
      quote: `The vendor lock-in was killing our innovation. AI Clean Core showed us how to build internal AI capabilities that we actually own. Now we're not just using AI—we're leading with it in our industry.`,
      author: "Michael Rodriguez",
      title: "Chief Executive Officer",
      company: "Manufacturing Plus",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face",
      metrics: "100% data sovereignty, 40% efficiency gains"
    },
    {
      id: 3,
      quote: `Their open-source approach wasn't just about cost savings—it was about strategic control. We now have AI systems that evolve with our business instead of forcing us to adapt to vendor roadmaps.`,
      author: "Dr. Emily Watson",
      title: "Chief Innovation Officer",
      company: "HealthTech Solutions",
      avatar: "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=80&h=80&fit=crop&crop=face",
      metrics: "Full regulatory compliance, 50% faster insights"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [testimonials?.length]);

  return (
    <section className="py-20 bg-gradient-to-br from-brand-primary to-blue-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Client Logos */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Trusted by Industry Leaders
            </h2>
            <p className="text-blue-200 text-lg">
              Organizations across sectors choose AI independence over vendor dependency
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
            {clientLogos?.map((client, index) => (
              <div key={index} className="group">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-all duration-300 border border-white/20">
                  <img 
                    src={client?.logo} 
                    alt={`${client?.name} logo`}
                    className="w-full h-12 object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                  />
                  <div className="text-xs text-blue-200 text-center mt-2">
                    {client?.industry}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              What Leaders Are Saying
            </h2>
            <p className="text-blue-200 text-lg">
              Real feedback from executives who chose AI independence
            </p>
          </div>

          <div className="relative">
            {/* Testimonial Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 lg:p-12 border border-white/20">
              <div className="text-center space-y-6">
                {/* Quote */}
                <div className="relative">
                  <Icon name="Quote" size={48} className="text-brand-secondary/30 mx-auto mb-6" />
                  <blockquote className="text-xl lg:text-2xl leading-relaxed text-white font-medium">
                    "{testimonials?.[currentTestimonial]?.quote}"
                  </blockquote>
                </div>

                {/* Author Info */}
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <img 
                    src={testimonials?.[currentTestimonial]?.avatar}
                    alt={testimonials?.[currentTestimonial]?.author}
                    className="w-16 h-16 rounded-full border-2 border-white/30"
                  />
                  <div className="text-center sm:text-left">
                    <div className="text-lg font-semibold text-white">
                      {testimonials?.[currentTestimonial]?.author}
                    </div>
                    <div className="text-blue-200">
                      {testimonials?.[currentTestimonial]?.title}
                    </div>
                    <div className="text-brand-secondary font-medium">
                      {testimonials?.[currentTestimonial]?.company}
                    </div>
                  </div>
                </div>

                {/* Metrics */}
                <div className="inline-flex items-center px-6 py-3 bg-white/10 rounded-full border border-white/20">
                  <Icon name="TrendingUp" size={16} className="text-brand-secondary mr-2" />
                  <span className="text-sm font-medium">
                    {testimonials?.[currentTestimonial]?.metrics}
                  </span>
                </div>
              </div>
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center space-x-2 mt-8">
              {testimonials?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial 
                      ? 'bg-brand-secondary' :'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentTestimonial((prev) => prev === 0 ? testimonials?.length - 1 : prev - 1)}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center border border-white/20 transition-all duration-300"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>

            <button
              onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length)}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center border border-white/20 transition-all duration-300"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Shield" size={24} className="text-brand-secondary" />
            </div>
            <div className="text-2xl font-bold mb-2">SOC 2 Type II</div>
            <div className="text-blue-200 text-sm">Enterprise Security</div>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Award" size={24} className="text-brand-secondary" />
            </div>
            <div className="text-2xl font-bold mb-2">ISO 27001</div>
            <div className="text-blue-200 text-sm">Information Security</div>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Users" size={24} className="text-brand-secondary" />
            </div>
            <div className="text-2xl font-bold mb-2">500+</div>
            <div className="text-blue-200 text-sm">Successful Implementations</div>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Clock" size={24} className="text-brand-secondary" />
            </div>
            <div className="text-2xl font-bold mb-2">24/7</div>
            <div className="text-blue-200 text-sm">Support & Monitoring</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProof;